import {
  CSSProperties,
  ReactNode,
  TouchEvent,
  WheelEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ArrowDownOutlined } from "@ant-design/icons";
import "./AutoScrollContainer.scss";

const DEFAULT_BOTTOM_THRESHOLD = 24;

export interface AutoScrollContainerRef {
  scrollToBottom: (instant?: boolean) => void;
  followTyping: () => void;
  resumeAutoScroll: (options?: {
    scrollToBottom?: boolean;
    instant?: boolean;
  }) => void;
}

interface AutoScrollContainerProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  isStreaming?: boolean;
  resumeButtonAriaLabel?: string;
  bottomThreshold?: number;
}

const AutoScrollContainer = forwardRef<
  AutoScrollContainerRef,
  AutoScrollContainerProps
>(
  (
    {
      children,
      className,
      style,
      isStreaming = false,
      resumeButtonAriaLabel = "Resume auto-scroll",
      bottomThreshold = DEFAULT_BOTTOM_THRESHOLD,
    },
    ref,
  ) => {
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const touchStartYRef = useRef<number | null>(null);
    const releaseProgrammaticLockRef = useRef<number | null>(null);
    const clearFollowActivityRef = useRef<number | null>(null);
    const hasFollowActivityRef = useRef(false);
    const isProgrammaticScrollRef = useRef(false);
    const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);

    const clearProgrammaticLock = useCallback(() => {
      if (releaseProgrammaticLockRef.current !== null) {
        window.clearTimeout(releaseProgrammaticLockRef.current);
        releaseProgrammaticLockRef.current = null;
      }

      isProgrammaticScrollRef.current = false;
    }, []);

    const markFollowActivity = useCallback(() => {
      hasFollowActivityRef.current = true;

      if (clearFollowActivityRef.current !== null) {
        window.clearTimeout(clearFollowActivityRef.current);
      }

      clearFollowActivityRef.current = window.setTimeout(() => {
        hasFollowActivityRef.current = false;
        clearFollowActivityRef.current = null;
      }, 420);
    }, []);

    const clearFollowActivity = useCallback(() => {
      if (clearFollowActivityRef.current !== null) {
        window.clearTimeout(clearFollowActivityRef.current);
        clearFollowActivityRef.current = null;
      }
      hasFollowActivityRef.current = false;
    }, []);

    const getDistanceFromBottom = useCallback((node: HTMLDivElement) => {
      return node.scrollHeight - node.scrollTop - node.clientHeight;
    }, []);

    const isNearBottom = useCallback(
      (node: HTMLDivElement) => {
        return getDistanceFromBottom(node) <= bottomThreshold;
      },
      [bottomThreshold, getDistanceFromBottom],
    );

    const scrollToBottom = useCallback(
      (instant: boolean = false) => {
        const node = viewportRef.current;
        if (!node) return;

        if (!instant) {
          isProgrammaticScrollRef.current = true;
        } else {
          isProgrammaticScrollRef.current = false;
        }

        node.scrollTo({
          top: node.scrollHeight,
          behavior: instant ? "auto" : "smooth",
        });

        if (releaseProgrammaticLockRef.current !== null) {
          window.clearTimeout(releaseProgrammaticLockRef.current);
          releaseProgrammaticLockRef.current = null;
        }

        if (!instant) {
          releaseProgrammaticLockRef.current = window.setTimeout(() => {
            clearProgrammaticLock();
          }, 140);
        }
      },
      [clearProgrammaticLock],
    );

    const handleResumeAutoScroll = useCallback(
      (options?: { scrollToBottom?: boolean; instant?: boolean }) => {
        setIsAutoScrollPaused(false);
        if (options?.scrollToBottom === false) return;
        scrollToBottom(options?.instant ?? true);
      },
      [scrollToBottom],
    );

    const followTyping = useCallback(() => {
      markFollowActivity();
      if (isAutoScrollPaused) return;
      scrollToBottom(true);
    }, [isAutoScrollPaused, markFollowActivity, scrollToBottom]);

    const pauseAutoScrollByUser = useCallback(() => {
      const canPause = isStreaming || hasFollowActivityRef.current;
      if (!canPause || isAutoScrollPaused) return;
      setIsAutoScrollPaused(true);
    }, [isAutoScrollPaused, isStreaming]);

    const handleWheel = useCallback(
      (event: WheelEvent<HTMLDivElement>) => {
        if (event.deltaY < 0) {
          pauseAutoScrollByUser();
        }
      },
      [pauseAutoScrollByUser],
    );

    const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
    }, []);

    const handleTouchMove = useCallback(
      (event: TouchEvent<HTMLDivElement>) => {
        const currentY = event.touches[0]?.clientY;
        if (currentY === undefined || touchStartYRef.current === null) {
          return;
        }

        if (currentY > touchStartYRef.current) {
          pauseAutoScrollByUser();
        }

        touchStartYRef.current = currentY;
      },
      [pauseAutoScrollByUser],
    );

    const handleTouchEnd = useCallback(() => {
      touchStartYRef.current = null;
    }, []);

    const handleScroll = useCallback(() => {
      const node = viewportRef.current;
      if (!node || isProgrammaticScrollRef.current) return;
      const canPause = isStreaming || hasFollowActivityRef.current;
      if (!canPause || isAutoScrollPaused) return;

      if (!isNearBottom(node)) {
        setIsAutoScrollPaused(true);
      }
    }, [isAutoScrollPaused, isNearBottom, isStreaming]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom,
        followTyping,
        resumeAutoScroll: handleResumeAutoScroll,
      }),
      [followTyping, handleResumeAutoScroll, scrollToBottom],
    );

    useEffect(() => {
      return () => {
        clearProgrammaticLock();
        clearFollowActivity();
      };
    }, [clearFollowActivity, clearProgrammaticLock]);

    return (
      <div className="auto-scroll-container">
        <div
          ref={viewportRef}
          className={`auto-scroll-container__viewport ${className ?? ""}`.trim()}
          style={style}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {children}
        </div>

        {isAutoScrollPaused && (
          <button
            type="button"
            className="auto-scroll-container__resume-btn"
            aria-label={resumeButtonAriaLabel}
            onClick={() => handleResumeAutoScroll({ instant: true })}
          >
            <ArrowDownOutlined />
          </button>
        )}
      </div>
    );
  },
);

AutoScrollContainer.displayName = "AutoScrollContainer";

export default AutoScrollContainer;
